import * as React from "react";
import { flushSync } from "react-dom";
import { Tooltip as TooltipPrimitive } from "radix-ui";

import { cn } from "@/lib/utils";
import { pinEnterStart, pinExitStart } from "@/components/ui/mui-grow";
import { spareFromRadixDismiss } from "@/components/ui/radix-dismiss";

// The tooltip every ported screen shows — MUI's, which is the spec here for the
// usual reason: the Timmy theme declares no `MuiTooltip` override, so MUI's own
// defaults are what a modsy user sees.
//
// **Why this sits beside `tooltip.tsx` rather than inside it.** That file is a
// vendored shadcn primitive, unmodified since the skeleton import and off-limits
// to restyling (`CLAUDE.md`, "Conventions") — and its look is not MUI's: a 6px
// radius, 12px type, `12px/6px` padding, `--foreground` ground, and an arrow it
// renders unconditionally. So this is the wrapper the same convention asks for,
// built on the Radix primitives directly because the arrow cannot be declined
// through the vendored `TooltipContent`.
//
// The donor reaches for two tooltips and they differ in exactly one respect,
// which is why the delay is a prop with two named constants rather than a
// default nobody reads:
//
//   - `@mui/material/Tooltip` **directly**, whose `enterDelay` is 100ms — the
//     finish chips (`FinishVariants.tsx:3,86`);
//   - `@modsy/react-ui`'s `Tooltip`, a thin wrapper that passes
//     `enterDelay={openDelay}` with `openDelay = 0` and `arrow={showCallout}`
//     with `showCallout = false` (`react-ui/src/tooltip/tooltip.tsx:18-20,78-79`)
//     — the preview card's name, the status chip, and the action menu's blocked
//     rows.
//
// Nothing styles `.len-tooltip`, so the wrapper changes the timing and the arrow
// and nothing about the paint.
//
// **The callout is per call site, not a default.** `showCallout` is off in the
// wrapper and two donors switch it on — the action menu's Delete-blocked row
// (`Header/ActionMenu.tsx:211-215`) and the table's Not Recommended label
// (`item-status-label/item-not-recommended.tsx:31-38`) — while the Add Variant
// row right beside the first one leaves it off. Two blocked rows on one menu
// that look different in the source is exactly the kind of thing a shared
// default would erase.

/** MUI's `enterDelay` default (`Tooltip.js:242`). */
export const MUI_TOOLTIP_DELAY_MS = 100;

/** `react-ui`'s `openDelay` default (`tooltip.tsx:20`) — it opens on contact. */
export const LEN_TOOLTIP_DELAY_MS = 0;

/**
 * MUI's tooltip surface, transcribed from `Tooltip.js:124-133`.
 *
 * `rgba(97,97,97,0.92)` is `alpha(grey[700], 0.92)` written out: a Tailwind
 * alpha modifier compiles to `color-mix(in oklab, …)`, which is both a different
 * colour and unreadable next to the number it came from. `text-[11px]` is
 * `pxToRem(11)`, `font-medium` is `fontWeightMedium`, and `px-2 py-1` is
 * `padding: '4px 8px'`.
 */
const MUI_TOOLTIP_SURFACE = cn(
  "z-50 rounded-[4px] bg-[rgba(97,97,97,0.92)] px-2 py-1 text-[11px] font-medium text-white",
  // `maxWidth: 300` and `wordWrap: 'break-word'`. A long finish name wraps
  // inside the box rather than stretching it across the page.
  "max-w-[300px] break-words",
  // **The box unfolds from the middle of the edge facing the trigger**, which
  // is MUI's rule and only MUI's: `transformOrigin` is keyed off
  // `[data-popper-placement*="top"]` and its three siblings
  // (`Tooltip.js:134-149`), so it depends on the side alone — `top-start` grows
  // from the same point `top` does, and the callout changes nothing but
  // `position` and `marginBlock` (`:150-157`). Radix's own
  // `--radix-tooltip-content-transform-origin` answers a different question:
  // with an arrow it uses the arrow's centre and tip, and without one it
  // follows `align`, so `align="start"` grows the Not Recommended label's box
  // from a point ~19px left of where the donor grows it and unfolds it
  // sideways. Invisible under the primitive's uniform 95% zoom; not invisible
  // under a 0.75 x 0.5625 one.
  // The four rules are in `index.css` beside the keyframes they serve, keyed
  // off Radix's `data-side` the way MUI's are off popper's placement.
  // **MUI's `Grow`, at the tooltip's own clock.** `Tooltip.js:615` hands the
  // transition `theme.transitions.duration.shorter` — 200ms
  // (`createTransitions.js:29`) — in both directions, and `Grow` splits that
  // across two: opacity over the whole 200ms, the transform over two thirds of
  // it entering (133ms) and over the same two thirds after a third's wait
  // leaving (`Grow.js:105-106,139-146`). The scale is not uniform either —
  // `getScale` squares the Y (`Grow.js:20-21`), so the box unfolds from
  // `scale(0.75, 0.5625)` rather than zooming from 95%.
  //
  // The keyframes are the ones the Snackbar and the menu paper already use
  // (`index.css`); only the clocks differ, so the tooltip carries its own pair
  // of tokens. What was here before was the vendored primitive's
  // `fade-in-0 zoom-in-95` — one 150ms clock, a uniform 95%, and 50ms short.
  //
  // **Deaf for the whole of the close, too.** MUI's popper carries
  // `pointerEvents: 'none'`, flipped to `'auto'` only by the variant that
  // wants `open && !disableInteractive` (`Tooltip.js:63-70`) — and `open` is
  // false from the first frame of the exit, so a box on its way out answers
  // nothing the pointer does to it. Radix keeps its content mounted and live
  // for the length of the animation instead, which is long enough to matter:
  // touching the fading box would tell this component the reader was on the
  // tooltip, and a title returning after that would bring back a box MUI had
  // already let go. The rule itself lives with the keyframes in `index.css`,
  // where `pointer-events: none` on `[data-state="closed"]` can carry the
  // `!important` it needs to beat the inline style `DismissableLayer` sets.
  "mui-tooltip-grow",
);

/**
 * How long MUI refuses the mouse after a touch contact on the same trigger.
 *
 * `ignoreNonTouchEvents` goes up on `touchstart` (`Tooltip.js:407-412`) and
 * comes down in exactly one place: the `closeTimer` inside `handleClose`, at
 * `duration.shortest` (`:328-330`). After a tap that close is itself the
 * `leaveTimer` `handleTouchEnd` starts at `leaveTouchDelay` (`:427-435`), so
 * the flag stands for both clocks past the finger lifting, and while it stands
 * `handleMouseOver` returns at `:336` without opening anything.
 */
const MUI_TOOLTIP_LEAVE_TOUCH_DELAY_MS = 1500;
const MUI_TOOLTIP_SHORTEST_MS = 150;
const MUI_TOOLTIP_MOUSE_AFTER_TOUCH_MS = MUI_TOOLTIP_LEAVE_TOUCH_DELAY_MS + MUI_TOOLTIP_SHORTEST_MS;

/**
 * The gap MUI leaves between the trigger and the box.
 *
 * Its placement rules set `marginBottom`/`marginTop`/`marginInline*` to `14px`
 * on the side facing the trigger (`Tooltip.js:134-150`), which is what Radix's
 * `sideOffset` is. `index.css` carries it again as the popper band's
 * `--mui-popper-gap`; change the two together.
 */
const MUI_TOOLTIP_GAP_PX = 14;

/**
 * MUI's flat `margin: 2`, which survives on the cross axis.
 *
 * `Tooltip.js:131` puts `margin: 2` on the tooltip div and the placement
 * selectors override only the side facing the trigger, to 14px (`:134-149`),
 * so the two cross-axis sides keep their 2px. The popper is shrink-to-fit, so
 * on a centred box those two margins cancel and nothing moves — which is why
 * this was first read as invisible and left out. It is not invisible on
 * `align="start"`, where the box is pinned to the reference's leading edge
 * and the donor's therefore begins 2px inside it. One call site reaches it:
 * the Not Recommended label (`item-badges.tsx:176`, donor `top-start`).
 * Found by the sixteenth parity pass. The popper band's `--mui-popper-margin`
 * in `index.css` is the same number.
 */
const MUI_TOOLTIP_MARGIN_PX = 2;

/**
 * The callout's size, **as MUI paints it**.
 *
 * MUI's arrow is a `1em x 0.71em` box, 11 x 7.81 against the tooltip's 11px,
 * but what shows is its `::before`: that same box rotated 45deg about one
 * corner and clipped to it (`Tooltip.js:188-208`, origin per placement at
 * `:75-92`). The visible part is a right-angled triangle whose hypotenuse is
 * the box's 1em width and whose depth is half that — 11 x 5.5. Radix's arrow
 * is an SVG triangle stretched to its box, so the box is sized to the
 * triangle. An earlier pass sized it to MUI's box instead (8 deep), which
 * drew a sharper callout reaching 2.5px nearer the trigger; found by #506's
 * parity review in the screenshot pair.
 */
const MUI_TOOLTIP_ARROW_WIDTH_PX = 11;
const MUI_TOOLTIP_ARROW_PX = 5.5;

interface MuiTooltipProps {
  /** The donor's `title`. A node, not a string — the Not Recommended tooltip
   * carries a link. */
  title: React.ReactNode;
  /** The trigger. */
  children: React.ReactNode;
  /**
   * The donor's `placement`. **`bottom` by default, as MUI's is** — the status
   * chip passes none and opens downward, while the three that pass
   * `placement='top'` say so.
   */
  side?: "top" | "bottom" | "left" | "right";
  /** One of the two constants above; which of the donor's two tooltips this
   * screen reaches for. */
  delayMs?: number;
  /**
   * The second half of the donor's `placement`: `top-start` is `side="top"` plus
   * `align="start"`. `center` by default, as MUI's bare `top`/`bottom` are.
   */
  align?: "start" | "center" | "end";
  /**
   * The donor's `showCallout` — MUI's `arrow`. Off by default, as the wrapper's
   * is.
   *
   * **The callout goes in the 14px gap; it does not replace it.** MUI's arrow
   * variant does carry `marginBlock: 0` (`Tooltip.js:150-156`), but it is
   * emitted as a flat declaration on the same generated class, at (0,1,0),
   * while the facing margin comes from a nested placement selector —
   * `.MuiTooltip-popper[data-popper-placement*="top"] &` (`:142-148`) — at
   * (0,3,0). The placement rule wins for the facing side, so `marginBlock: 0`
   * only strips the flat `margin: 2` from the other block side. The donor's
   * arrow'd box therefore stands the same 14px off its trigger as any other,
   * its 7.81px arrow box hanging into that gap (`bottom: 0; margin-bottom:
   * -0.71em`, `:84-86`) and the triangle it paints reaching 5.5px of it, so
   * the tip stops 8.5px short.
   */
  arrow?: boolean;
  /** The donor's `maxWidth`, when a call site narrows the 300px default
   * (`ItemStatusChip` passes 180). */
  maxWidthPx?: number;
  /**
   * Whether the trigger *is* `children` rather than a button wrapped around it.
   * Needed wherever the child is already interactive — a chip is a `<button>`,
   * and nesting one inside another is invalid markup that also loses the chip's
   * accessible name.
   */
  asChild?: boolean;
  /**
   * The donor's `closeDelay` — MUI's `leaveDelay`. **0 by default, as both
   * are** (`react-ui/src/tooltip/tooltip.tsx:21,80`; `Tooltip.js:247`). One
   * call site passes one: the Not Recommended label, at 200
   * (`item-not-recommended.tsx:37`).
   *
   * It is how long the box waits after the reader has gone, not whether
   * reaching the box keeps it: every box here is interactive, as every donor
   * box is — see "The close is MUI's" below.
   */
  leaveDelayMs?: number;
  /**
   * R1 (#506, owner-ruled, a named break): the keyboard's box is not the
   * pointer's to close. With it set, a box raised by a ringed focus stays when
   * the pointer leaves, for as long as the ring holds on the trigger or inside
   * it. **Blocked menu rows only** (#506 D1): the donor's are `disabled` and
   * never reach this state, and everywhere else it does reach it, and closes.
   */
  holdForKeyboard?: boolean;
  /**
   * The first of the donor's popper `offset` pair — **skidding**, px along the
   * trigger, as popper.js reads `[skidding, distance]`. 0 by default, as MUI's
   * is. One call site passes one: the address link, at 150
   * (`address-link.tsx`, `offset={[150, -10]}`).
   *
   * **Through floating-ui, so collisions still apply.** Popper runs its
   * `offset` before `preventOverflow`, so near the window's edge the donor pulls
   * a skidded box back on screen — the Homesite drawer is anchored right. Radix's
   * `alignOffset` is that same cross-axis offset, but floating-ui applies it
   * only to a `start` or `end` placement (`@floating-ui/core`'s `offset`: `if
   * (alignment && typeof alignmentAxis === 'number')`), so a skidded box is
   * placed as `align="start"` at the offset that puts a centred box `skid`
   * along: `skid + (trigger − box) / 2`, from the two lengths once the box has
   * mounted. Centred and side-placed callers are untouched.
   */
  skidPx?: number;
  /**
   * The second — **distance**, px added to MUI's 14px gap; negative pulls the
   * box in. 0 by default. The address link's is −10, which leaves its box 4px
   * below the link, as measured on the donor. Radix's `sideOffset` is exactly
   * this axis, so it is added there.
   */
  distancePx?: number;
  /** Extra classes for the box. */
  className?: string;
  /** Extra classes for the trigger — only meaningful without `asChild`, where
   * the trigger is a button this component owns. */
  triggerClassName?: string;
  "data-testid"?: string;
}

export function MuiTooltip({
  title,
  children,
  side = "bottom",
  align = "center",
  arrow = false,
  leaveDelayMs = 0,
  holdForKeyboard = false,
  delayMs = LEN_TOOLTIP_DELAY_MS,
  maxWidthPx,
  asChild,
  skidPx = 0,
  distancePx = 0,
  className,
  triggerClassName,
  "data-testid": testId,
}: MuiTooltipProps) {
  // **An empty title keeps the tooltip mounted and shut**, rather than handing
  // the child back bare. MUI's `Tooltip` clones its child and adds no element
  // of its own, and an empty title only forces `open = false` — "There is no
  // point in displaying an empty tooltip" (`Tooltip.js:456-459`). A caller that
  // instead stopped rendering this component around the child would change the
  // element *type* at that position, and React would tear the child down and
  // build a new one: a focused control loses the keyboard, an animation
  // restarts, an uncontrolled input empties. The donor's does none of that,
  // because the wrapper never leaves.
  const silent = title === undefined || title === null || title === "";
  // Read by the content's dismissal guard below, which needs to know whether a
  // press landed on the trigger.
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  /** The same element as state, so the capture stopper follows a swapped child. */
  const [triggerNode, setTriggerNode] = React.useState<HTMLButtonElement | null>(null);
  const attachTrigger = React.useCallback((node: HTMLButtonElement | null) => {
    triggerRef.current = node;
    setTriggerNode(node);
  }, []);
  // And by the exit pin, which has to read the box while it is still open.
  const contentRef = React.useRef<HTMLDivElement>(null);
  /** The same element as state, so the popper's listeners follow each mount. */
  const [contentNode, setContentNode] = React.useState<HTMLDivElement | null>(null);
  const attachContent = React.useCallback((node: HTMLDivElement | null) => {
    contentRef.current = node;
    setContentNode(node);
  }, []);
  /** MUI's `leaveTimer` — see "The close is MUI's" below. */
  const leaveTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = React.useState(false);
  /** Whether the pointer is on the trigger — see the note below. */
  const [hovering, setHovering] = React.useState(false);
  /** Whether the keyboard is on it — the other half of the same note. */
  const [focusVisible, setFocusVisible] = React.useState(false);
  /** What `silent` was on the previous render — see the note below. */
  const [prevSilent, setPrevSilent] = React.useState(silent);
  // True for exactly as long as one press event is being dispatched — see
  // `markPress` and the `onOpenChange` refusal below.
  const pressing = React.useRef(false);
  // The same, for a focus the browser draws no ring for — see the trigger's
  // `onFocus`.
  const quietFocus = React.useRef(false);
  // And for a blur, whose close is the leave timer's — see the trigger's
  // `onBlur`.
  const blurring = React.useRef(false);
  /**
   * Whether the box up now is the keyboard's — R1's "a box opened by keyboard
   * focus". Raised by a ringed focus on the trigger and lowered by any close
   * that lands or by a blur, so a box the reader dismissed with `Escape` and
   * the pointer then raised again is the pointer's, ring or not.
   */
  const keyboardHolds = React.useRef(false);
  /**
   * Which kind of contact the trigger last saw.
   *
   * `mouseover` carries no `pointerType`, and a tap fires the compatibility
   * mouse events after its pointer ones — so the re-arm below would answer a
   * tap if nothing told it not to. MUI has the same problem and answers it
   * with `ignoreNonTouchEvents`, raised on a touch and lowered
   * `duration.shortest` after a close (`Tooltip.js:336-337,408,329`).
   *
   * Written on `pointerover` rather than on entry, because that is the same
   * idea with a lifetime that cannot go stale: `pointerover` refires for
   * every nested element and lands immediately before the `mouseover` it
   * guards, where `pointerenter` fires once per trigger and never again.
   * MUI's flag is time-boxed and this one is not, which is the same answer
   * only while nothing outlives the box — a mouse resting inside a trigger
   * that is then tapped on a hybrid device would leave a once-per-entry ref
   * reading `touch` for as long as the pointer stayed put, and refuse every
   * later crossing the donor answers ~150ms after the tap's close.
   */
  const lastPointerType = React.useRef("mouse");
  /**
   * Until when the mouse gets no answer on this trigger, because a finger did.
   *
   * `Infinity` while the contact is down, as MUI's flag is, then a deadline
   * once it lifts — see `MUI_TOOLTIP_MOUSE_AFTER_TOUCH_MS`. Read rather than
   * timed out, so nothing has to be cleaned up when the trigger unmounts.
   */
  const mouseRefusedUntil = React.useRef(0);
  const touchIsSpeaking = () => Date.now() < mouseRefusedUntil.current;
  // When the box the pointer is waiting on is due, and the timer that will
  // raise it if Radix's own has been thrown away — see `keepEnterDelayRunning`.
  const enterDueAt = React.useRef<number | null>(null);
  const enterTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * Whether that kept deadline has passed with the box still owed.
   *
   * State and not a ref, so the decision belongs to a render: the timer's own
   * closure is from the render the press happened in, and the title can move
   * underneath it. Reading a stale `silent` there paints an empty grey box —
   * MUI paints nothing for an empty title however true its `openState` is
   * (`Tooltip.js:456-459`), and on `/items` the press *is* what empties the
   * title, because it starts the load.
   */
  const [deadlineReached, setDeadlineReached] = React.useState(false);
  /**
   * Whether the box was up at the moment the title emptied.
   *
   * The two ways a title can go quiet look identical from `hovering` and read
   * completely differently in MUI. A box that was up is `openState` still
   * true under an empty title, and it comes straight back when the title does
   * (`Tooltip.js:456-459` is the only thing that changed). A deadline that was
   * still pending is `openState` *false* with `enterTimer` running, and it
   * owes the reader a box on its original schedule, not on the restoration.
   * State rather than a ref because the render body decides on it, and refs
   * may not be read there.
   */
  const [openWhenSilenced, setOpenWhenSilenced] = React.useState(false);

  /**
   * Raise a flag for the length of the event being dispatched.
   *
   * `composeEventHandlers` runs the handler passed *in* before the primitive's
   * own, so the flag is up by the time Radix asks for the change, and Radix's
   * setter calls `onOpenChange` synchronously in controlled mode
   * (`react-use-controllable-state`: the controlled branch invokes the change
   * handler inline rather than from an effect). The microtask that lowers it
   * runs after this one event's dispatch has finished, so the window is exactly
   * this event and needs no timer, no `pointerup` bookkeeping and no cleanup on
   * a press that never becomes a click.
   */
  const raiseForThisEvent = (flag: React.RefObject<boolean>) => {
    flag.current = true;
    queueMicrotask(() => {
      flag.current = false;
    });
  };

  /** Mark the trigger press Radix is about to close on. */
  const markPress = () => raiseForThisEvent(pressing);

  /** Forget a pending enter delay and the timer standing in for it. */
  const clearEnterDelay = () => {
    enterDueAt.current = null;
    if (enterTimer.current !== null) {
      clearTimeout(enterTimer.current);
      enterTimer.current = null;
    }
  };

  /**
   * Keep MUI's enter delay running across a press.
   *
   * Radix's trigger closes on the press through its *click* handler, which is
   * unconditional (`react-tooltip/dist/index.mjs:206`, `onClick:
   * composeEventHandlers(props.onClick, context.onClose)` — its `onPointerDown`
   * at `:195-198` calls `onClose` only when the box is already open). That
   * `handleClose` clears the open timer, and with the box still shut the
   * controlled setter compares `false` against a prop already reading `false`
   * and never calls `onOpenChange` — so the refusal that keeps every other
   * press from closing the box cannot reach this one, and the box the reader
   * was waiting on simply never arrives. MUI has no such hole: its
   * `enterTimer` is started by `handleMouseOver` (`Tooltip.js:348-353`) and
   * nothing in a press touches it, so the box appears on schedule with the
   * pointer still where it was.
   *
   * So the deadline is ours to keep. The pointer's arrival records when the box
   * is due; a press that lands before then re-arms it for the remainder. Its
   * other caller is the `mouseover` re-arm on the trigger below, for the same
   * reason from the other end — there, Radix's own timer is the one that will
   * never start. It cannot double-open: every caller returns early once `open`
   * is true, and the render that acts on the deadline drops it if the box is
   * up by the time it lands.
   */
  const keepEnterDelayRunning = () => {
    if (open || enterDueAt.current === null || enterTimer.current !== null) return;
    const remaining = Math.max(0, enterDueAt.current - Date.now());
    enterTimer.current = setTimeout(() => {
      enterTimer.current = null;
      if (enterDueAt.current !== null) setDeadlineReached(true);
    }, remaining);
  };

  /**
   * Give up the claim on the box, whoever was holding it.
   *
   * MUI's `handleClose` zeroes `openState` (`Tooltip.js:316-331`) and every
   * close it has routes through that one door, so a box that goes down is a
   * box no reader is still owed. Both callers below are that door: the close
   * that lands, and the reader leaving the box itself.
   */
  const forgetReader = () => {
    setHovering(false);
    setFocusVisible(false);
    // A close that lands takes the pending deadline with it, or a box
    // dismissed with `Escape` would spring back on the next press.
    setDeadlineReached(false);
    clearEnterDelay();
  };

  // **The close is MUI's: a timer started by the reader leaving, cancelled by
  // their coming back** (#506). `handleMouseLeave` starts
  // `leaveTimer.start(leaveDelay, handleClose)` (`Tooltip.js:356-361`), and
  // `handleMouseOver` clears it (`:346-347`). Both are wired on the trigger
  // *and*, because `disableInteractive` defaults false, on the popper
  // (`:240,515-516,609`). So the box goes `leaveDelay` after the pointer has
  // left both, whether or not it ever moves again, and stays for as long as
  // the pointer rests on either.
  //
  // The popper is the half Radix has no counterpart for. MUI's has **no gap to
  // cross**: the 14px is margin on the tooltip div *inside* the popper
  // (`Tooltip.js:131,134-150`), and the absolutely positioned popper
  // shrink-wraps around it, so trigger and popper touch. Radix pushes its
  // content away with `sideOffset` into empty space. The band that closes that
  // gap is a pseudo-element on Radix's un-scaled popper wrapper, in
  // `index.css`, and the listeners below are on that wrapper for the same
  // reason: the content itself is under `Grow`'s scale, and a band on it
  // would have a hole in it for the first 133ms.
  //
  // **Radix's own pointer closer is kept out of it.** Its hoverable content
  // draws a grace polygon when the pointer leaves the trigger or the box, and
  // closes the moment a `pointermove` lands outside it
  // (`react-tooltip/dist/index.mjs:244-296`). That is the rule that let a
  // pointer which left and *stopped* keep the box forever. Turning hoverable
  // content off (`disableHoverableContent`) is not the way out: Radix would
  // then close on the trigger's leave itself (`:146-152`), and refusing that
  // close in `onOpenChange` comes too late, because the primitive tells the
  // provider "closed" *before* it asks us (`:90-99`). The provider's skip
  // window would start at the leave rather than at the close, and a reader who
  // rested on a box for more than 800ms would then wait out a finish chip's
  // 100ms, where MUI's `hystersisOpen` still stands (`Tooltip.js:305,321`).
  // So hoverable content stays on and the polygon is simply never drawn: the
  // two native `pointerleave` listeners that draw it (`:262-266`) are stopped
  // by capture-phase listeners on the same elements, which run first at the
  // target. React's own `onPointerLeave` rides `pointerout`, so nothing else
  // is lost.
  const clearLeaveTimer = () => {
    if (leaveTimer.current !== null) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
  };

  const startLeaveTimer = () => {
    clearLeaveTimer();
    leaveTimer.current = setTimeout(() => {
      leaveTimer.current = null;
      // **R1 (#506, owner-ruled): the keyboard's box is not the pointer's to
      // close**, where the call site asks (`holdForKeyboard`, blocked menu
      // rows only — #506 D1). A box the keyboard holds (`keyboardHolds`), on a trigger
      // still holding a `:focus-visible` ring — or, for a blocked menu row,
      // whose wrapper is the trigger, a ring on the row inside it — stays when
      // the pointer goes. A box the pointer raised after the keyboard's was
      // dismissed is the pointer's, and closes like any other. The donor never meets this: its
      // blocked rows are `disabled` and outside the roving walk (#456 D2 is
      // what put Atlas's inside it). Read when the timer fires rather than
      // when it starts, and only a real leave or blur ever starts it, which is
      // what the attempt on #479 got wrong. A named break in the ledger.
      //
      // The veto is on *closing a box*, and only while one is up. With none up
      // — the title emptied under a reader resting on the box — the timer
      // still forgets the reader, as `handleClose` does in the donor
      // (`Tooltip.js:316-331`), so the box does not come back with the title.
      // Read off the DOM because that is the state at the moment of firing;
      // the render this closure came from may be several behind.
      const trigger = triggerRef.current;
      const box = contentRef.current;
      const boxUp = box !== null && box.getAttribute("data-state") !== "closed";
      const ringHeld =
        trigger !== null &&
        (trigger.matches(":focus-visible") || trigger.querySelector(":focus-visible") !== null);
      if (holdForKeyboard && boxUp && ringHeld && keyboardHolds.current) return;
      // **Closed through the primitive, not past it**, so the close reaches the
      // provider when it happens and its skip window starts where MUI's does,
      // at the close. Radix's trigger-scoped scroll close is the one door that
      // closes this tooltip and no other: its capture listener closes a box
      // whose trigger lies inside the scrolled element (`index.mjs:311-319`).
      // `scroll` does not bubble, so of the app's other listeners only capture
      // ones on an ancestor hear it — TanStack Router's scroll restoration,
      // which records a position that has not moved — and another Radix
      // tooltip whose trigger lies *inside* this one would close with it; no
      // caller nests one today. The close then arrives in `onOpenChange` like
      // every other and takes the one door there. `forgetReader` runs as well,
      // because `handleClose` zeroes `openState` whether or not a box was up
      // (`Tooltip.js:316-331`), and with none up the event above lands nowhere.
      trigger?.dispatchEvent(new Event("scroll"));
      forgetReader();
    }, leaveDelayMs);
  };

  const onPopperOver = React.useEffectEvent(() => clearLeaveTimer());
  const onPopperLeave = React.useEffectEvent(() => startLeaveTimer());
  // The popper's focus pair, beside its mouse pair (`Tooltip.js:520-525`):
  // `handleFocus` cancels the close only for a ringed focus (`:393`), and
  // `handleBlur` starts it for any blur whose target has no ring (`:366`). So
  // a reader who clicks the Not Recommended box's spreadsheet link — a new
  // tab, and the tab switch blurs the link — comes back to no box, pointer
  // resting on it or not.
  const onPopperFocus = React.useEffectEvent((event: FocusEvent) => {
    if (event.target instanceof Element && event.target.matches(":focus-visible")) {
      clearLeaveTimer();
    }
  });
  const onPopperBlur = React.useEffectEvent((event: FocusEvent) => {
    if (!(event.target instanceof Element) || !event.target.matches(":focus-visible")) {
      startLeaveTimer();
    }
  });

  // In a layout effect so the listeners are in place at the commit that
  // mounts the box, before any pointer can reach it.
  React.useLayoutEffect(() => {
    const trigger = triggerNode;
    const popper = contentNode?.parentElement;
    if (!trigger || !contentNode || !popper) return;
    // Only the element's own leave, which is the one Radix listens for: a
    // capture listener also sees the leaves of everything inside it, and those
    // belong to whoever listens on them.
    const keepPolygonUndrawn = (event: Event) => {
      if (event.target === event.currentTarget) event.stopImmediatePropagation();
    };
    const over = () => onPopperOver();
    const leave = () => onPopperLeave();
    const focusIn = (event: FocusEvent) => onPopperFocus(event);
    const focusOut = (event: FocusEvent) => onPopperBlur(event);
    trigger.addEventListener("pointerleave", keepPolygonUndrawn, { capture: true });
    contentNode.addEventListener("pointerleave", keepPolygonUndrawn, { capture: true });
    popper.addEventListener("pointerover", over);
    popper.addEventListener("pointerleave", leave);
    popper.addEventListener("focusin", focusIn);
    popper.addEventListener("focusout", focusOut);
    return () => {
      trigger.removeEventListener("pointerleave", keepPolygonUndrawn, { capture: true });
      contentNode.removeEventListener("pointerleave", keepPolygonUndrawn, { capture: true });
      popper.removeEventListener("pointerover", over);
      popper.removeEventListener("pointerleave", leave);
      popper.removeEventListener("focusin", focusIn);
      popper.removeEventListener("focusout", focusOut);
    };
  }, [triggerNode, contentNode]);

  // `skidPx` — see the prop. The box's own length along the trigger,
  // measured once it mounts: a skid is `align="start"` at `skid + (trigger −
  // box) / 2`, which is where a centred box skidded by `skid` begins.
  const [boxLength, setBoxLength] = React.useState<number | null>(null);
  const [triggerLength, setTriggerLength] = React.useState<number | null>(null);
  const along = side === "top" || side === "bottom";
  React.useLayoutEffect(() => {
    if (skidPx === 0 || !contentNode || !triggerNode) return;
    // The box's border-box length, fractional and untouched by `Grow`'s scale;
    // the trigger's from its rect, which nothing scales — and which, unlike an
    // observer entry, an inline trigger such as a link has at all. The
    // observer reports on attach, before the first paint, and again whenever
    // the box or a block-level trigger resizes.
    //
    // An inline trigger's observer never reports a change, so its rect is
    // read again on every window resize too — which is when popper re-reads
    // its reference (its `eventListeners` modifier), and when a wrapping
    // address rewraps.
    const measureTrigger = () => {
      const rect = triggerNode.getBoundingClientRect();
      setTriggerLength(along ? rect.width : rect.height);
    };
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target !== contentNode) continue;
        const [size] = entry.borderBoxSize;
        setBoxLength(along ? size.inlineSize : size.blockSize);
      }
      measureTrigger();
    });
    observer.observe(contentNode);
    observer.observe(triggerNode);
    window.addEventListener("resize", measureTrigger);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measureTrigger);
    };
  }, [skidPx, contentNode, triggerNode, along]);
  const skidOffset =
    skidPx === 0 || boxLength === null || triggerLength === null
      ? null
      : skidPx + (triggerLength - boxLength) / 2;

  React.useEffect(() => clearLeaveTimer, []);

  // **An empty title closes the box for real; the pointer keeps its place
  // underneath.** MUI recomputes only its render-time `open`
  // (`Tooltip.js:456-459`) while `openState` goes on tracking the pointer, so
  // the box leaves and comes back if the title returns before the pointer
  // does. Two things fall out of closing rather than withholding the content:
  //
  //   - the box leaves the way MUI's does. Radix animates on
  //     `data-state="closed"`, which only a close produces, where unmounting
  //     the content takes the box away in a single frame with no exit at all;
  //   - the prop handed to `Root` stays equal to the state held here. Shadow
  //     one with the other and Radix's setter drops every close it raises,
  //     because it compares the next value against the prop it was last
  //     rendered with (see `onOpenChange`).
  //
  // Whether the reader is still on the trigger is the one thing this component
  // has to remember for itself, because it is the part of MUI's `openState`
  // that outlives the box. Both ways of being on it, since MUI opens on either
  // and closes on neither: the pointer (`Tooltip.js:332-355`) and the
  // keyboard.
  //
  // The *trigger* is what counts, even though MUI's popper is interactive
  // (`Tooltip.js:240,506-517,609`) and does keep `openState` alive while the
  // reader is on the box. It keeps it only while the box is up: an empty title
  // flips the popper to `pointerEvents: 'none'` (`:63-70`), taking it out of
  // hit-testing under the reader's own cursor fires a `mouseleave` on it, and
  // the listener MUI has there answers that like any other leave
  // (`:516,358,324`). Walked on the running donor to be sure, since reading it
  // both ways is easy: park the pointer on a sort hint's box and force a load,
  // and the donor's box goes and does not return. The keyboard half is `:focus-visible` and not merely focus,
  // because that is the test MUI's own `handleFocus` makes (`:385-406`,
  // `isFocusVisible(event.target)`) — a trigger focused by a mouse press was
  // never opened by that focus, so it must not be reopened by it either.
  //
  // Both halves are for the one call site whose title comes and goes, the
  // `/items` usage-count sort hint: press it or tab to it and press `Enter`,
  // and the sort blanks the title and fills it again with the direction the
  // reader pressed to find out. Neither half is visible on that screen today,
  // and for reasons outside this component both times — the keyboard's because
  // Radix closes on the scroll that focusing a control performs, the pointer's
  // because that table covers its own header with a loading overlay while the
  // sort runs, so the reader is taken off the trigger before the title returns
  // where the donor leaves them on it. Both are measured and recorded in
  // `docs/migration/parity/phase-1-item-catalog.md`; what is pinned here is
  // the rule, which the tests walk directly.
  //
  // Adjusted during render on the `silent` edge only, so an open refused while
  // silent never becomes one to close and every other close stays Radix's own.
  // The press-kept enter timer is the one thing here that outlives a render.
  React.useEffect(() => clearEnterDelay, []);

  // **An empty title does not stop MUI's clock, so it must not stop ours.**
  // `enterTimer` is started by `handleMouseOver` (`Tooltip.js:348-353`) and
  // nothing about the title touches it or `openState`; only the render-time
  // `open` is recomputed (`:456-459`). So a title that empties and returns
  // inside the enter delay still opens on the *original* deadline. Ours has
  // to be armed at the moment it empties, because Radix's is about to be
  // lost: while `silent` stands, `onOpenChange` refuses the open Radix's own
  // timer will ask for, and Radix does not ask twice. Reachable at the one
  // call site with a delay whose title empties — the usage-count sort hint,
  // which empties it on every load it starts (`item-table.tsx:447-451`).
  // Raised as blocking by the second cross-review run.
  // Armed on the edge only. Re-arming on any other render would restart a
  // deadline MUI lets run, so the previous value is tracked here rather than
  // handed to a dependency array — the effect wants one transition, not one
  // identity.
  const wasSilent = React.useRef(silent);
  React.useEffect(() => {
    if (silent && !wasSilent.current) keepEnterDelayRunning();
    wasSilent.current = silent;
  });

  // **The empty-title edge, in a layout effect rather than in the render.**
  // This close is the one MUI raises from a prop rather than from an event
  // (`Tooltip.js:456-459` recomputes the render-time `open` when the title
  // goes), so there is no handler to pin the exit from the way `onOpenChange`
  // pins every other close. Deciding it during render left none either: React
  // re-renders a render-phase update before it commits, so the box went from
  // open to closed in one commit and `pinExitStart` had no moment at which the
  // live transform was still readable — a box cut short mid-`Grow` snapped out
  // to full size and only then shrank, where the donor's shrinks from the size
  // it had reached (`Grow.js:116-152` writes the transition and then the
  // target and forces no reflow, so the exit interpolates from the current
  // computed value). Run from a layout effect, the commit underneath still
  // holds the open box, the pin reads it, and the `setOpen(false)` here
  // flushes before the browser paints — so nothing of the intermediate state
  // is ever on screen. Found by the seventeenth parity pass.
  React.useLayoutEffect(() => {
    if (prevSilent === silent) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the state move is the point: it has to happen in the commit *after* the one the pin reads, and before paint
    setPrevSilent(silent);
    if (silent) {
      setOpenWhenSilenced(open);
      // Also what keeps the property honest: it is inline and outlives the
      // close that set it, and every close now writes it, so a box can never
      // exit from an earlier close's partial value.
      if (open) pinExitStart(contentRef.current);
      setOpen(false);
    } else if (openWhenSilenced && (hovering || focusVisible)) {
      // The restore is an open like any other, and lands inside the 200ms
      // often enough to matter: the sort hint's title comes back while its own
      // box is still going out.
      pinEnterStart(contentRef.current);
      setOpen(true);
    }
  }, [prevSilent, silent, openWhenSilenced, hovering, focusVisible, open]);

  // The kept deadline has passed, and opening is still a decision: the reader
  // must still be on the trigger, and the title must still say something. An
  // empty title keeps the claim pending rather than dropping it, which is
  // MUI's own shape — its `openState` is true and waiting, so the box appears
  // if the title comes back before the reader leaves.
  //
  // In a layout effect for the same reason the silent edge above is: this is
  // the third writer of `open`, and an open decided during render cannot pin
  // the enter, because it cannot read the DOM. That mattered — the box this
  // path raises is often one still going out (dismiss with `Escape`, then
  // cross from a badge's icon onto its label inside the 200ms), and without a
  // pin it jumped back to the keyframes' own `from` and replayed the whole
  // enter. Worse when an earlier close had pinned: the property is inline and
  // outlives the open that wrote it, so the box jumped *down* to a value from
  // a previous gesture. Found by the nineteenth parity pass.
  React.useLayoutEffect(() => {
    if (deadlineReached && (open || !hovering)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- see above: the pin has to read the DOM before this state moves
      setDeadlineReached(false);
    } else if (deadlineReached && !silent) {
      setDeadlineReached(false);
      pinEnterStart(contentRef.current);
      setOpen(true);
    }
  }, [deadlineReached, open, hovering, silent]);

  return (
    <TooltipPrimitive.Root
      delayDuration={delayMs}
      open={open}
      // **The tooltip does not close on a press — on any trigger.** MUI's
      // `Tooltip` attaches no click or press handler of any kind: the complete
      // set of props it puts on the cloned child is `onTouchStart`,
      // `onTouchEnd`, `onMouseOver`, `onMouseLeave`, `onFocus`, `onBlur`, and
      // `onMouseMove` only under `followCursor` (`Tooltip.js:483-525`), and the
      // `react-ui` wrapper adds none (`tooltip.tsx:74-100`). So in modsy a
      // stationary pointer keeps the box up however many times the control
      // under it is clicked — which is what a user does when the thing they
      // clicked looks dead and did nothing. Radix's trigger disagrees, closing
      // on both `onPointerDown` and `onClick`
      // (`react-tooltip/dist/index.mjs:195`, `:206`).
      //
      // **Refused here rather than cancelled at the trigger.** The trigger's
      // handlers are composed with `composeEventHandlers`, whose opt-out is
      // `preventDefault` — and cancelling a press's default is not something a
      // *shared* tooltip may do to its callers: MUI cancels nothing, so a press
      // on a tooltip'd link must still navigate, on a submit button still
      // submit, on text still start a selection, and focus still land where the
      // browser puts it. Cancelling would also skip the same handler's
      // `isPointerDownRef` bookkeeping (`index.mjs:170,199-201`), which is what
      // stops the focus a mouse press moves from *opening* a tooltip — MUI
      // opens on focus only when the target is `:focus-visible`
      // (`Tooltip.js:392`), so a press must neither close one nor open one.
      // Owning `open` lets Radix's handler run in full and answers only the
      // close it asks for.
      //
      // Every close MUI has is still here. Its list is exhaustively four —
      // pointer leave after `leaveDelay` (`:356-361`), a blur whose target is
      // not `:focus-visible` (`:363-384`), `Escape` while open (`:437-452`),
      // and touch end after `leaveTouchDelay` (`:427-435`). The first two are
      // this component's leave timer (see "The close is MUI's"), `Escape` is
      // Radix's own dismissal, and the fourth has no counterpart: Radix has no
      // touch path at all
      // (`index.mjs:185` bails on `pointerType === "touch"`), so a long-press
      // opens nothing here where the donor opens after 700ms: a standing gap
      // this guard neither causes nor closes, carried as a named parity break
      // rather than quietly implied away by the list above.
      onOpenChange={(next) => {
        if (!next && pressing.current) return;
        // The blur's close, which MUI runs on the leave timer rather than at
        // once — see the trigger's `onBlur`. The timer closes through this
        // same handler once it fires, with the flag long down.
        if (!next && blurring.current) return;
        // **A focus with no ring on it opens nothing.** MUI runs its focus open
        // through `isFocusVisible(event.target)` (`Tooltip.js:392-393`), which
        // is `element.matches(':focus-visible')` verbatim
        // (`@mui/utils/isFocusVisible:10-21`), so a trigger that takes focus
        // quietly raises no box: a `focus()` call putting the keyboard back
        // where it was, or the window the reader alt-tabs into, which refires
        // `focus` on whatever held it. Radix has no such test — it opens on any
        // focus outside a press (`index.mjs:202-204`) — and the press half of
        // that is `isPointerDownRef`, which covers only the press itself.
        if (next && quietFocus.current) return;
        // "There is no point in displaying an empty tooltip"
        // (`Tooltip.js:456-459`). Refusing the open is what lets the content
        // stay mounted for the animation above without ever painting an empty
        // box on the way in.
        if (next && silent) return;
        // The same refusal, on the path Radix drives: its `onPointerMove` open
        // (`index.mjs:183-190`) does not know a finger was just here.
        if (next && touchIsSpeaking()) return;
        // **And where the enter is pinned**, for the same reason mirrored:
        // `Grow` re-opens a box that is still closing from the size the exit
        // had reached (`Grow.js:75-113` writes a new transition over the live
        // computed state), where a keyframe would jump back to
        // `scale(0.75, 0.5625)` and replay the whole enter. Reachable on every
        // call site — leave a trigger and come back inside the 200ms, or
        // dismiss with `Escape` and cross inside the trigger again. Null when
        // nothing is mounted, which is the ordinary open and wants the
        // keyframes' own `from`. Found by the eighteenth parity pass.
        if (next) pinEnterStart(contentRef.current);
        // **Every close is one door, as MUI's is.** `handleClose` zeroes
        // `openState` (`Tooltip.js:316-331`) and every one of MUI's closes
        // routes through it — `Escape` (`:436-453`) and pointer leave
        // (`:356-361`) included — so once the box is down, a title returning
        // cannot bring it back: the empty-title override lifts off a state
        // that is already false. Remembering the reader only until they leave
        // the trigger is not the same rule and comes apart on the closes that
        // land while they are still on it: dismiss the sort hint with
        // `Escape`, press `Enter`, and the box the reader just dismissed would
        // return with the sort. So a close that actually lands forgets them,
        // and only a fresh enter or focus says they are back.
        if (!next) {
          // **Where the exit is pinned.** `Grow` interpolates its exit from the
          // element's current computed state (`Grow.js:140-152`), so a box cut
          // short mid-open shrinks away from the size it had reached, where a
          // CSS animation's `from` frame is a constant and the box would snap
          // out to full size first. This runs in the event that closes, before
          // the render that flips `data-state` and takes the live value away.
          pinExitStart(contentRef.current);
          forgetReader();
          keyboardHolds.current = false;
        }
        // **`flushSync`, because the primitive compares against the prop it was
        // last rendered with.** Radix's controlled setter drops a change whose
        // value equals that prop (`react-use-controllable-state`: `if (value2
        // !== prop) onChange(value2)`), and the prop only moves on a render. A
        // blur and the focus that replaces it are two dispatches inside one
        // React batch, so without a flush between them the close lands, the
        // re-open is compared against a prop still reading `true`, and the box
        // stays down for good: the reason a keyboard user walked onto a blocked
        // menu row vanished the moment they pressed `Enter` on it.
        //
        // Unconditional, because the comparison that drops the change is
        // against that last-rendered prop and not against `open` as this
        // handler reads it: a `next !== open` guard here would let through
        // exactly the batch that needs the flush.
        flushSync(() => setOpen(next));
      }}
    >
      <TooltipPrimitive.Trigger
        ref={attachTrigger}
        asChild={asChild}
        onPointerDown={(event) => {
          lastPointerType.current = event.pointerType;
          if (event.pointerType === "touch") mouseRefusedUntil.current = Infinity;
          markPress();
          if (event.pointerType !== "touch") keepEnterDelayRunning();
        }}
        onPointerUp={(event) => {
          if (event.pointerType === "touch") {
            mouseRefusedUntil.current = Date.now() + MUI_TOOLTIP_MOUSE_AFTER_TOUCH_MS;
          }
        }}
        onPointerCancel={(event) => {
          if (event.pointerType === "touch") {
            mouseRefusedUntil.current = Date.now() + MUI_TOOLTIP_MOUSE_AFTER_TOUCH_MS;
          }
        }}
        onClick={markPress}
        // `pointerType` is filtered exactly where Radix filters it
        // (`react-tooltip/dist/index.mjs:185`): a touch contact fires
        // `pointerenter` before `pointerdown`, so without this the kept
        // deadline becomes the *only* opener on touch — and the wrong one.
        // MUI answers a tap with neither: it opens on `onTouchStart` after
        // `enterTouchDelay` 700ms and closes `leaveTouchDelay` 1500ms after
        // `onTouchEnd` (`Tooltip.js:414-435`), so a tap shorter than 700ms
        // raises nothing at all. Having no touch path is D3, already carried;
        // having a wrong one would be worse than the gap it fills.
        onPointerOver={(event) => {
          lastPointerType.current = event.pointerType;
          // `handleMouseOver`'s `leaveTimer.clear()` (`Tooltip.js:347`), on
          // every refire, as MUI's is.
          clearLeaveTimer();
        }}
        onPointerEnter={(event) => {
          // A finger is still speaking for this trigger; MUI answers no mouse
          // event of any kind until it has stopped (`Tooltip.js:336-337`).
          if (event.pointerType !== "touch" && touchIsSpeaking()) return;
          setHovering(true);
          if (event.pointerType !== "touch") {
            enterDueAt.current = Date.now() + delayMs;
            // Radix's own timer is the opener for every ordinary hover, and
            // deliberately so — an open it raises arms the provider's skip
            // window where one of ours does not (D13). The exception is a
            // claim made while the title is empty: that open would be refused
            // and Radix does not ask twice, so here ours has to carry it.
            if (silent) keepEnterDelayRunning();
          }
        }}
        // **A close does not end the hover; the next move inside the trigger
        // starts it again.** MUI listens on `mouseover` rather than
        // `mouseenter` and says why at `Tooltip.js:308-310` — the event
        // refires for every nested element, and `handleMouseOver` is guarded
        // by nothing but a disabled child and the touch flag
        // (`:332-337,511-512`), so each refire re-arms `enterTimer` and opens
        // again. Concretely, in the donor: dismiss a badge's box with
        // `Escape`, then slide the pointer from the badge's icon onto its
        // label without ever leaving the badge — the box comes back.
        //
        // Radix cannot answer that on its own. Its re-arm is gated by
        // `hasPointerMoveOpenedRef`, set on the first arming move and reset
        // only in `onPointerLeave` (`react-tooltip/dist/index.mjs:184-193`),
        // so with the pointer still inside, no further move arms anything.
        // Hence our own timer, and hence the `enterDueAt` test: a claim
        // already running is MUI's `open` skip (`:311`), not a restart. What
        // stays carried is the restart itself — MUI clears and re-starts the
        // *whole* enter delay on each refire (`:346,348-353`), where this
        // records the deadline once per entry. Matching that means refusing
        // Radix's own on-schedule open until our deadline passes, which takes
        // the enter clock away from `delayDuration` for all 22 call sites; it
        // is a named break (D14) rather than a line here, and it is invisible
        // on the twenty sites whose delay is 0.
        //
        // The same `enterDueAt` test carries a second named break, **D15**: a
        // trigger that *appears* under a resting cursor fires `mouseover` with
        // no `pointermove` behind it, so MUI opens (`:346-353` falls straight
        // to `handleOpen` at a 0ms delay) and Radix, whose only pointer path
        // needs motion (`index.mjs:183-190`), cannot. This handler is reached
        // — `pointerover`/`pointerenter` fire on the appearance too — and
        // declines it, because `pointerenter` has already recorded the
        // deadline. Arming a timer here anyway would answer it, and would also
        // make our timer the opener for *every* hover, since it is armed at
        // `mouseover` and Radix's not until the first move after it. That
        // hands D13 the whole app: an open this component raises itself never
        // reaches Radix's setter, so the provider's 800ms skip window would
        // stop arming at all and both `MUI_TOOLTIP_DELAY_MS` sites would wait
        // their full delay after every close — a worse trade than the case it
        // buys.
        onMouseOver={() => {
          if (lastPointerType.current === "touch" || touchIsSpeaking() || open) return;
          // The claim is recorded whatever the title says. MUI's
          // `handleMouseOver` has no title test at all: it goes to
          // `handleOpen`, which sets `openState` true regardless
          // (`Tooltip.js:304-315`), and an empty title only forces the
          // *render-time* `open` back to false (`:456-459`) — so the box
          // appears the instant the title returns, with the pointer never
          // having left. Refusing the claim here instead would lose the
          // reader on the one call site whose title comes and goes.
          setHovering(true);
          if (enterDueAt.current !== null) return;
          // Recorded even while the title is empty, and for the same reason:
          // MUI's clock starts on the crossing, not on the title. A claim made
          // with nothing to say still matures on schedule, and the box is owed
          // the moment the title returns. Our timer has to carry it, because
          // Radix's open would be refused while `silent` stands and Radix does
          // not ask twice — so this is the one caller that must arm during a
          // silent window rather than despite it.
          enterDueAt.current = Date.now() + delayMs;
          keepEnterDelayRunning();
        }}
        onPointerLeave={() => {
          setHovering(false);
          setDeadlineReached(false);
          clearEnterDelay();
          startLeaveTimer();
        }}
        onFocus={(event) => {
          const visible = event.target.matches(":focus-visible");
          setFocusVisible(visible);
          if (!visible) raiseForThisEvent(quietFocus);
          // A ringed focus is `handleMouseOver` in MUI (`Tooltip.js:393-405`),
          // and that clears a pending close (`:347`) — a blur and the refocus
          // that replaces it leave the box standing.
          else {
            clearLeaveTimer();
            keyboardHolds.current = true;
          }
        }}
        // **A blur is a leave, on the leave's clock.** MUI's `handleBlur`
        // sends any blur the target is not `:focus-visible` for — which after
        // a blur is every blur — straight into `handleMouseLeave`
        // (`Tooltip.js:363-384`), which starts `leaveTimer` like a pointer
        // leave (`:356-361`) and reaches `handleClose`, zeroing `openState`
        // (`:316-331`). So tabbing off a trigger ends the claim even with the
        // pointer still on it, `leaveDelay` later — 200ms on the Not
        // Recommended label, the next task everywhere else. Radix closes on
        // the blur itself (`index.mjs:205`), so that close is refused for
        // this one event (see `onOpenChange`) and the timer is the closer.
        // The timer forgets the reader even with no box up, which is the
        // title-emptied case: Radix's own close compares `false` against a
        // prop already reading `false` and never reaches `onOpenChange`, and
        // without the forget the reader who tabs away mid-load would get the
        // box back when the title returns.
        //
        // A blur the trigger's own child has declined (`BlockedMenuItem`, for
        // the blur its press causes) closes nothing, as before; it only
        // forgets the reader.
        onBlur={(event) => {
          if (event.defaultPrevented) {
            forgetReader();
            return;
          }
          keyboardHolds.current = false;
          raiseForThisEvent(blurring);
          startLeaveTimer();
        }}
        // **Radix does not type its own trigger.** It renders `Primitive.button`
        // with no `type`, which in HTML means `type="submit"` — so a tooltip
        // hung on a *non-interactive* thing (a badge, a chip) becomes a submit
        // button, and inside a `<form>` a click on it sends the form. The Add
        // Variant dialog reached exactly that: its preview card draws
        // `ItemStatusOverlay` (`item-badges.tsx:208`) inside the form, so
        // clicking a pending badge submitted a completed draft and created a
        // variant. MUI's `Tooltip` has no such hazard — it clones its child and
        // adds no element of its own.
        //
        // Only when this component owns the button: with `asChild` the trigger
        // *is* the caller's element, and `type` on a `<span>` is meaningless.
        type={asChild ? undefined : "button"}
        data-slot="tooltip-trigger"
        data-testid={testId}
        className={triggerClassName}
      >
        {children}
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          ref={attachContent}
          data-slot="mui-tooltip-content"
          data-testid="mui-tooltip"
          // Read by the popper band in `index.css`: the callout's
          // `marginBlock: 0` changes which sides of MUI's popper carry a margin.
          data-arrow={arrow ? "" : undefined}
          // The second of Radix's two press-close paths, and the reason
          // answering either one alone reads as no fix: the content is a
          // dismissable layer, and a press on the *trigger* is a press outside
          // that layer (`index.mjs:327-329`), so it dismisses even once the
          // root above has refused the trigger's own close.
          //
          // Only a press *on the trigger* is spared. A press genuinely outside
          // still dismisses, and costs nothing to part with: the pointer has
          // by definition left the trigger, and MUI closes on that leave
          // anyway. This cancels a Radix custom event and no DOM default, so a
          // trigger that is a link or a submit button keeps doing what it does.
          //
          // No `onPointerLeave` here: leaving the box is the popper's leave,
          // and the popper's listeners are the wrapper's (see "The close is
          // MUI's"). That is also what forgets a reader the box goes out from
          // under: an empty title flips MUI's popper to `pointerEvents: 'none'`
          // (`Tooltip.js:63-70`), Chromium reports the loss of hit-testing
          // under a resting cursor as a leave, and the popper's
          // `handleMouseLeave` (`:515-516`) runs the timer into `handleClose`,
          // which zeroes `openState` (`:316-331`) — so the donor's box does not
          // come back for a reader who was on it. Walked on the running donor.
          //
          // **Nor is a press on the band.** It is MUI's popper, whose margin it
          // is (`Tooltip.js:131,134-150`), and MUI answers a press there with
          // nothing (`:505-525`). The dismissable layer is the box inside it,
          // so the band reads to Radix as outside; its target is the wrapper
          // itself, the pseudo-element having no node of its own.
          onPointerDownOutside={(event) => {
            const target = event.target as Node;
            if (triggerRef.current?.contains(target)) event.preventDefault();
            if (target === contentRef.current?.parentElement) event.preventDefault();
          }}
          // **`Escape` closes the box and goes on**, as MUI's does: a
          // document listener that closes and consumes nothing
          // (`Tooltip.js:436-452`). Radix's layer would consume it, so an
          // `Escape` that closed a reason standing over a leaving menu inside a
          // dialog was spent there. The donor's closes the dialog too (#718).
          // A key a menu has already consumed stays consumed: that leaves the
          // box standing, as the donor's modal root does (#479 item 3).
          onEscapeKeyDown={(event) => spareFromRadixDismiss(event)}
          side={side}
          // A skidded box is positioned from its start edge — see `skidOffset`.
          align={skidPx === 0 ? align : "start"}
          // Radix's default `sticky="partial"` adds floating-ui's `limitShift`,
          // which keeps the box overlapping its trigger — so a short address's
          // 150px skid is cut back to the link's end. Popper's tether only
          // ever widens its viewport clamp (`preventOverflow.js:91-97`), so
          // while the link is on screen the donor's box is clamped to the
          // viewport alone and sits the full 150px along. `always` drops the
          // limiter; `shift` still keeps the box on screen.
          sticky={skidPx === 0 ? "partial" : "always"}
          // MUI's `margin: 2` on the cross axis — see the constant. Zero when
          // the box is centred, because there the two sides cancel.
          alignOffset={
            skidPx !== 0
              ? (skidOffset ?? 0)
              : align === "center" || (arrow && (side === "left" || side === "right"))
                ? 0
                : MUI_TOOLTIP_MARGIN_PX
          }
          // The offset lands on Radix's positioned wrapper, which is MUI's
          // popper's opposite number, and the painted box is flush with it:
          // the wrapper's `min-width: max-content` cannot exceed the box,
          // because the box's own `max-width` caps what its max-content
          // contribution can be. Measured settled on both `align="start"`
          // widths, the box begins 2.00px inside its trigger — this margin
          // and nothing else. (The sixteenth pass read 24.5px here and
          // carried it as a divergence; it had measured a box mid-`Grow`,
          // where `getBoundingClientRect` reports the scaled rect.)
          // Radix's offset is `sideOffset + arrowHeight`
          // (`react-popper/dist/index.mjs:104`), so the callout's own height
          // comes out of the gap rather than adding to it — which is what
          // MUI's negative arrow margin does too. Both leave the box 14px off
          // its trigger.
          sideOffset={
            (arrow ? MUI_TOOLTIP_GAP_PX - MUI_TOOLTIP_ARROW_PX : MUI_TOOLTIP_GAP_PX) + distancePx
          }
          // MUI keeps the callout 4px clear of the box's ends — `{ name:
          // 'arrow', options: { element: arrowRef, padding: 4 } }`
          // (`Tooltip.js:541-548`) — where Radix's popper defaults that to 0
          // (`react-popper/dist/index.mjs:64`) and lets a collision-shifted box
          // draw its callout into its own 4px corner.
          //
          // The number is MUI's; the mechanism is not the same one. Popper
          // clamps the arrow into the padded range and still draws it, while
          // floating-ui reports the clamp as a non-zero `centerOffset` and
          // Radix answers that by hiding the arrow outright
          // (`react-popper/dist/index.mjs:172,224-229`). So in the band where
          // the donor draws a callout tucked into the corner, this draws none.
          // Out of reach today: the clamp bites only once the trigger's
          // centre falls within `arrowWidth / 2 + padding` — 9.5px — of the
          // box's end, which after a collision shift means within 9.5px of the
          // clipping boundary, and none of the three arrow'd call sites gets
          // near it (a blocked menu row's centre is half a paper inside the
          // viewport, and the Not Recommended label would have to be all but
          // scrolled out of the table). Identical to MUI everywhere the clamp
          // does not bite, which is why the number is still MUI's.
          arrowPadding={4}
          className={cn(
            MUI_TOOLTIP_SURFACE,
            // A skidded box is placed from its own length, which the observer
            // reports only after the first layout — and React commits that
            // report in a later task, after a paint. Hidden until then, so no
            // frame shows it at the unskidded start edge. The `Grow` keyframes
            // run on regardless; that one frame is at its near-zero opacity.
            skidPx !== 0 && skidOffset === null && "invisible",
            className,
          )}
          style={maxWidthPx === undefined ? undefined : { maxWidth: `${maxWidthPx}px` }}
        >
          {title}
          {/* MUI's arrow is a rotated square clipped to a triangle, filled
              with `alpha(grey[700], 0.9)` — a hair more opaque than the box's
              own 0.92, and carried as its own number rather than shared with
              it (`Tooltip.js:188-208`). Its size is above. */}
          {arrow ? (
            <TooltipPrimitive.Arrow
              width={MUI_TOOLTIP_ARROW_WIDTH_PX}
              height={MUI_TOOLTIP_ARROW_PX}
              className="fill-[rgba(97,97,97,0.9)]"
            />
          ) : null}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
